import styles from "./CampaignsScreen.module.css";

// Ported from the prototype's GroupedBars / Funnel (design_handoff demo-screens.jsx
// ~L2750/L2786). Reusable so Dashboard can adopt the same chart primitives later;
// they live with Campaigns for now (the only consumer) per the agent's scope.

export interface BarSeries {
  key: string;
  label: string;
  color: string;
}

export interface BarRow {
  /** x-axis label for the column (e.g. "W1"). */
  label: string;
  /** stable react key for the column (e.g. the week-start ISO date). */
  id: string;
  /** value per series key. */
  values: Record<string, number>;
}

/** Grouped vertical bars: one column per row, one bar per series. */
export function GroupedBars({
  data,
  series,
  height = 132,
  fmt,
}: {
  data: BarRow[];
  series: BarSeries[];
  height?: number;
  fmt?: (n: number) => string;
}) {
  const max = Math.max(1, ...data.flatMap((r) => series.map((s) => r.values[s.key] ?? 0)));
  return (
    <div className={styles.gbars}>
      <div className={styles.gbarsPlot} style={{ minHeight: `${height}px` }}>
        {data.map((r) => (
          <div className={styles.gbCol} key={r.id}>
            <div className={styles.gbBars}>
              {series.map((s) => {
                const v = r.values[s.key] ?? 0;
                return (
                  <div
                    key={s.key}
                    className={styles.gbBar}
                    style={{ height: `${Math.max(2, (v / max) * 100)}%`, background: s.color }}
                  >
                    <span className={styles.gbTip}>
                      {s.label}: {fmt ? fmt(v) : v}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className={styles.gbX}>{r.label}</div>
          </div>
        ))}
      </div>
      <div className={styles.chartLegend}>
        {series.map((s) => (
          <span key={s.key} className={styles.cl}>
            <span className={styles.clDot} style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface FunnelStep {
  label: string;
  value: number;
  color: string;
}

/** Tapering funnel: aligned label rows on the left, an SVG trapezoid stack on the right. */
export function Funnel({ steps }: { steps: FunnelStep[] }) {
  const max = steps[0] ? steps[0].value : 1;
  const N = steps.length;
  const W = 100; // viewBox units
  const w = steps.map((s) => Math.max(7, (s.value / (max || 1)) * W));
  return (
    <div className={styles.funnelChart}>
      <div className={styles.fcRows}>
        {steps.map((s, i) => {
          const prev = steps[i - 1];
          const conv = prev ? Math.round((s.value / (prev.value || 1)) * 100) : null;
          return (
            <div className={styles.fcRow} key={s.label}>
              <div className={styles.fcName}>
                <span className={styles.fcDot} style={{ background: s.color }} />
                {s.label}
              </div>
              <div className={styles.fcVal}>
                {s.value.toLocaleString()}
                {conv !== null && <span className={styles.fcConv}>{conv}%</span>}
              </div>
            </div>
          );
        })}
      </div>
      <svg
        className={styles.fcSvg}
        viewBox={`0 0 ${W} 100`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {steps.map((s, i) => {
          const top = w[i] ?? 7;
          const bot = (i < N - 1 ? w[i + 1] : (w[i] ?? 7) * 0.8) ?? 7;
          const bandH = 100 / N;
          const y0 = i * bandH + 1.2;
          const y1 = (i + 1) * bandH - 1.2;
          const tl = (W - top) / 2;
          const tr = (W + top) / 2;
          const bl = (W - bot) / 2;
          const br = (W + bot) / 2;
          return (
            <polygon
              key={s.label}
              points={`${tl},${y0} ${tr},${y0} ${br},${y1} ${bl},${y1}`}
              fill={s.color}
            />
          );
        })}
      </svg>
    </div>
  );
}
